/**
 * A chamada em curso.
 *
 * **Store, e não estado de componente, por um motivo específico:** o objeto
 * `Room` do LiveKit carrega a conexão WebRTC viva. Se ele morar na árvore de
 * componentes, trocar de tela no Desk desmonta o componente e derruba a ligação
 * no meio — é o bug mais comum de interface de chamada, e o mais constrangedor
 * de descobrir com cliente na linha.
 *
 * Aqui o `Room` sobrevive à navegação, e quem desenha a interface apenas lê.
 */
import { create } from "zustand";
import type { Room } from "livekit-client";

export interface ChamadaRecebida {
  callId: string;
  fromNumber?: string | null;
  contactName?: string | null;
}

type Situacao = "ociosa" | "tocando" | "conectando" | "em_chamada" | "encerrando";

interface CallState {
  situacao: Situacao;
  /** Quem está chamando, enquanto toca. */
  recebida: ChamadaRecebida | null;
  /** A chamada que estamos atendendo. */
  callId: string | null;
  room: Room | null;
  mudo: boolean;
  erro: string | null;
  /** Quando a conversa começou, para o cronômetro. */
  inicio: number | null;

  tocar: (chamada: ChamadaRecebida) => void;
  pararDeTocar: (callId?: string) => void;
  conectando: (callId: string) => void;
  conectada: (room: Room) => void;
  setMudo: (mudo: boolean) => void;
  /** A chamada terminou do outro lado — desliga tudo aqui também. */
  terminouLaFora: (callId?: string) => void;
  falhou: (erro: string) => void;
  encerrar: () => void;
}

export const useCallStore = create<CallState>((set, get) => ({
  situacao: "ociosa",
  recebida: null,
  callId: null,
  room: null,
  mudo: false,
  erro: null,
  inicio: null,

  tocar: (chamada) => {
    // Já em conversa: uma segunda chamada não pode roubar a tela de quem está
    // falando com um cliente.
    if (get().situacao === "em_chamada" || get().situacao === "conectando") return;
    set({ situacao: "tocando", recebida: chamada, erro: null });
  },

  pararDeTocar: (callId) => {
    const atual = get();
    if (atual.situacao !== "tocando") return;
    // Ignora o "parou" de outra chamada que não é a que está na tela.
    if (callId && atual.recebida?.callId !== callId) return;
    set({ situacao: "ociosa", recebida: null });
  },

  conectando: (callId) => set({ situacao: "conectando", callId, erro: null }),

  conectada: (room) =>
    set({ situacao: "em_chamada", room, inicio: Date.now(), recebida: null }),

  setMudo: (mudo) => set({ mudo }),

  terminouLaFora: (callId) => {
    const atual = get();
    if (atual.situacao === "ociosa") return;
    // Só reage ao fim da chamada que está nesta tela. Sem esta guarda, o fim
    // de uma ligação de outro atendente derrubaria a nossa.
    const minha = atual.callId ?? atual.recebida?.callId;
    if (callId && minha && callId !== minha) return;
    // `encerrar` desconecta a sala: sem isso o microfone fica aberto e a
    // tela segue contando o tempo de uma ligação que já acabou.
    get().encerrar();
  },

  falhou: (erro) =>
    set({ situacao: "ociosa", erro, room: null, callId: null, recebida: null }),

  encerrar: () => {
    const { room } = get();
    // Desconectar é obrigatório: sem isto o microfone continua aberto e a
    // sala segue contando minuto do outro lado.
    void room?.disconnect();
    set({
      situacao: "ociosa", room: null, callId: null,
      recebida: null, mudo: false, inicio: null,
    });
  },
}));
